<template>
  <view-wrapper
    class="h-full space-y-6"
    :class="{
      'm-0': !isView,
    }"
    :custom-margin="!isView"
  >
    <!-- view header -->
    <div v-if="isView && !isWorkflowBuilder && !isBacktestReport">
      <lf-h2 class="mb-1-5">{{ $t("SCORECARDS.SCORECARDS_MAIN_TITLE") }}</lf-h2>
      <span>
        {{ $t("SCORECARDS.SCORECARDS_MAIN_DESCRIPTION") }}
      </span>
    </div>
    <div v-if="isWorkflowBuilder">
      <lf-h2 class="mb-0">
        {{ $t("WORKFLOWS.WORKFLOW_BUILDER.SCORECARDS_MAIN_TITLE") }}
      </lf-h2>
    </div>

    <!-- filters/controls -->
    <div class="flex items-center justify-between">
      <div class="flex items-center">
        <lf-tab
          v-for="tab in tabs"
          :key="tab"
          :active="currentTab === tab"
          :data-cy="`tab-${tab}`"
          @click="selectTab(tab)"
        >
          {{ ROUTE_QUERY_TO_TAB_NAME[tab] }}
        </lf-tab>
      </div>
      <div class="flex h-10 space-x-4">
        <view-switch v-if="!isBacktestReport" v-model="activeView" />
        <div>
          <search-input v-model="params.search" no-padding />
        </div>
        <primary-button v-if="showNewTemplateButton" @click="handleNewClick">
          {{
            currentTab === tabs[0]
              ? $t("SCORECARDS.NEW_TEMPLATE")
              : $t("SCORECARDS.NEW_SC_GROUP")
          }}
        </primary-button>

        <lf-primary-button-with-dropdown
          v-show="showRunButton"
          :dropdown-items="runScDropdownItems"
          @button:click="handleRunClick"
        >
          <template #button>
            <span>
              {{
                isRunSpecific[currentTab]
                  ? $t("COMMON.RUN")
                  : $t("SCORECARDS.RUN_ALL")
              }}
            </span>
            <span v-if="isRunSpecific" class="h-5 w-5 rounded-full bg-blue-500">
              {{ currentViewComponentRef?.selectedIds?.length || 0 }}
            </span>
          </template>
        </lf-primary-button-with-dropdown>

        <workflow-builder-statuses-button v-if="isWorkflowBuilder" />
      </div>
    </div>

    <component
      :is="currentViewComponent"
      ref="currentViewComponentRef"
      :active-view="activeView"
      :params="params"
      :enable-selection="isSelectionEnabled"
      :show-action-menu="canUseScorecardActions"
      @update-params="updateParams"
      @new-template="handleNewClick"
    />

    <scorecard-type-modal v-if="showModal" :close="() => (showModal = false)" />
  </view-wrapper>
</template>
<script setup lang="ts">
import {
  computed,
  inject,
  onBeforeMount,
  onUnmounted,
  provide,
  ref,
  watch,
} from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";

import { storeToRefs } from "pinia";

import { AnalyticsEvent } from "@/enums/analytics";
import { Ability, PermissionSubject } from "@/enums/auth";
import {
  SCORECARD_VIEW_SCORECARDS_TAB,
  SCORECARD_VIEW_SCORECARD_GROUPS_TAB,
  VIEW_TYPE_LIST,
} from "@/helpers/constants";
import {
  ScorecardsIsBacktestReport,
  ScorecardsIsViewKey,
  ScorecardsIsWorkflowBuilder,
} from "@/helpers/symbols";
import { useAuth, usePermissions } from "@/hooks/auth";
import { useDealsBase } from "@/hooks/deals";
import { useNotification } from "@/hooks/notifications";
import { useScorecards, useScorecardsBase } from "@/hooks/scorecards";
import { useActiveStage, useActiveWorkflowTemplate } from "@/hooks/workflow";
import {
  ROUTE_PAGE_NOT_FOUND,
  ROUTE_SCORECARDS_GROUP_NEW,
} from "@/router/routes";
import { useAnalyticsStore } from "@/stores/analytics";
import useScorecardsStore from "@/stores/scorecards";

import WorkflowBuilderStatusesButton from "@/components/deals/workflow/WorkflowBuilderStatusesButton.vue";
import ScorecardGroups from "@/components/scorecards/ScorecardGroups.vue";
import ScorecardTypeModal from "@/components/scorecards/ScorecardTypeModal.vue";
import Scorecards from "@/components/scorecards/Scorecards.vue";
import LfPrimaryButtonWithDropdown from "@/components/ui/buttons/LfPrimaryButtonWithDropdown.vue";
import ViewSwitch from "@/components/ui/buttons/ViewSwitch.vue";

import type { VIEW_TYPE_TILE } from "@/helpers/constants";
import type { IClient } from "@/models/clients";
import type {
  IRunScorecardGroupsPayload,
  IRunScorecardsPayload,
  IScorecardsViewParams,
  ScorecardTab,
} from "@/models/scorecards";

const emit = defineEmits<{
  (eventName: "update-selected-scorecards", selectedScorecards: string[]): void;
  (
    eventName: "update-selected-scorecard-groups",
    selectedScorecardGroups: string[]
  ): void;
}>();

const props = defineProps({
  isView: {
    type: Boolean,
    default: true,
  },
  isWorkflowBuilder: {
    type: Boolean,
    default: false,
  },
});

provide(ScorecardsIsViewKey, props.isView);
provide(ScorecardsIsWorkflowBuilder, props.isWorkflowBuilder);
const isBacktestReport = inject(ScorecardsIsBacktestReport, false);

const { canUseScorecardActions } = useScorecards(false);
const { scorecardsData, scorecardGroupsData } = useScorecardsBase();
const router = useRouter();
const route = useRoute();
const { showMessage } = useNotification();
const { activeDeal } = useDealsBase();
const { t } = useI18n();
const { dispatch, getters } = useStore();
const { isLendflowUser } = useAuth();
const { canPerformActionReactive } = usePermissions();
const { activeStage } = useActiveStage();
const { canEditWorkflowTemplate } = useActiveWorkflowTemplate();
const store = useScorecardsStore();
const { viewParams: params } = storeToRefs(store);
const { trackEvent } = useAnalyticsStore();

const canCreateScorecard = canPerformActionReactive(
  PermissionSubject.scoreCard,
  Ability.create,
  {}
);

const canRunAnyScoreCard = canPerformActionReactive(
  PermissionSubject.scoreCard,
  Ability.runAny,
  { application: activeDeal.value.id, scoreCardStageId: activeStage.value?.id }
);

const isWorkflowLive = !props.isView && !props.isWorkflowBuilder;

const ROUTE_QUERY_TO_TAB_NAME: Record<string, string> = {
  [SCORECARD_VIEW_SCORECARDS_TAB]: t("SCORECARDS.TITLE"),
  [SCORECARD_VIEW_SCORECARD_GROUPS_TAB]: t("SCORECARDS.SC_GROUPS"),
};

const getInitialCurrentTabValue = (): ScorecardTab => {
  const name = route.query.activeTab?.toString() as ScorecardTab;
  const scorecardTabs = Object.keys(
    ROUTE_QUERY_TO_TAB_NAME
  ) as Array<ScorecardTab>;
  const initialTab = scorecardTabs.find((key) => key === name);
  return initialTab || SCORECARD_VIEW_SCORECARDS_TAB;
};

const getAppIdParam = () =>
  props.isView || props.isWorkflowBuilder
    ? ""
    : route.params?.id?.toString() || "";

const tabs = ref(Object.keys(ROUTE_QUERY_TO_TAB_NAME));
const currentTab = ref(getInitialCurrentTabValue());
const showModal = ref(false);
const isRunSpecific = ref({
  scorecards: false,
  scorecardGroups: false,
});
const currentViewComponentRef = ref<InstanceType<
  typeof Scorecards | typeof ScorecardGroups
> | null>(null);

const activeView = ref<typeof VIEW_TYPE_TILE | typeof VIEW_TYPE_LIST>(
  VIEW_TYPE_LIST
);

const isSelectionEnabled = computed(
  () =>
    (props.isWorkflowBuilder && canEditWorkflowTemplate.value) ||
    isRunSpecific.value[currentTab.value]
);

const activeClient = computed<IClient>(
  () => getters["auth/authClientSettings"]
);

const isScorecardsTab = computed(
  () => currentTab.value === SCORECARD_VIEW_SCORECARDS_TAB
);

const isScorecardGroupsTab = computed(
  () => currentTab.value === SCORECARD_VIEW_SCORECARD_GROUPS_TAB
);

const showNewTemplateButton = computed(
  () =>
    canCreateScorecard.value &&
    props.isView &&
    !props.isWorkflowBuilder &&
    !isBacktestReport
);

const showRunButton = computed(
  () =>
    !!currentViewComponentRef.value &&
    canRunAnyScoreCard.value &&
    !props.isView &&
    ((isScorecardsTab.value && scorecardsData.value?.data?.length) ||
      (isScorecardGroupsTab.value && scorecardGroupsData.value?.data?.length))
);

const currentViewComponent = computed(() =>
  isScorecardsTab.value ? Scorecards : ScorecardGroups
);

const selectedIds = computed(
  () => currentViewComponentRef.value?.selectedIds || []
);

const runScDropdownItems = computed(() => [
  {
    label: isScorecardsTab.value
      ? t("SCORECARDS.RUN_ALL")
      : t("SCORECARD_GROUPS.RUN_ALL"),
    handler: handleRun,
  },
  {
    label: isScorecardsTab.value
      ? t("SCORECARDS.RUN_SPECIFIC")
      : t("SCORECARD_GROUPS.RUN_SPECIFIC"),
    handler: handleRunSpecificClick,
  },
]);

const updateParams = (newParams: IScorecardsViewParams) => {
  params.value = { ...newParams };
};

const handleNewClick = () => {
  if (currentTab.value === tabs.value[0]) {
    showModal.value = true;
    return;
  }
  router.push({ name: ROUTE_SCORECARDS_GROUP_NEW });
};

const selectTab = (tab: string) => {
  trackEvent(AnalyticsEvent.ScorecardsTabChanged, {
    from_tab: currentTab.value,
    to_tab: tab,
  });
  currentTab.value = tab as ScorecardTab;
  router.replace({ ...route, query: { ...route.query, activeTab: tab } });
  store.clearViewParams();
  params.value.application_id = getAppIdParam();
  activeView.value = VIEW_TYPE_LIST;
};

const handleRunClick = async () => {
  if (!activeStage.value) {
    return;
  }
  if (!isRunSpecific.value[currentTab.value]) {
    await handleRun();
    trackEvent(AnalyticsEvent.ScorecardRun, {
      run_type: "all",
      selected_count: 0,
    });
    return;
  }
  if (!selectedIds.value.length) {
    showMessage(t("SCORECARDS.SELECT_TO_RUN"), "error");
    return;
  }
  const runData: IRunScorecardGroupsPayload | IRunScorecardsPayload =
    isScorecardsTab.value
      ? {
          score_card_ids: selectedIds.value,
          stage_id: activeStage.value.id,
        }
      : {
          score_card_group_ids: selectedIds.value,
          stage_id: activeStage.value.id,
        };

  await handleRun(runData);
  trackEvent(AnalyticsEvent.ScorecardRun, {
    run_type: "specific",
    selected_count: selectedIds.value.length,
  });
  isRunSpecific.value[currentTab.value] = false;
  if (!currentViewComponentRef.value) {
    return;
  }
  currentViewComponentRef.value.selectedIds = [];
};

const handleRun = (
  data: IRunScorecardGroupsPayload | IRunScorecardsPayload = {
    stage_id: activeStage.value?.id || "",
  }
) => {
  const actionName = isScorecardsTab.value
    ? "scorecards/runScorecards"
    : "scorecards/runScorecardGroups";

  const payload: {
    appId: string;
    data: IRunScorecardsPayload | IRunScorecardGroupsPayload;
  } = {
    appId: activeDeal.value.id,
    data,
  };
  return dispatch(actionName, payload);
};

const handleRunSpecificClick = () => {
  isRunSpecific.value[currentTab.value] = true;
};

watch([() => props.isView, () => route.params?.id], () => {
  if (props.isWorkflowBuilder) {
    return;
  }
  params.value.application_id = getAppIdParam();
});

watch(activeView, (newView, oldView) => {
  if (oldView && newView !== oldView) {
    trackEvent(AnalyticsEvent.ScorecardsViewChanged, {
      from_view: oldView,
      to_view: newView,
    });
  }
});

watch(selectedIds, (newValue: string[]) => {
  if (
    (props.isWorkflowBuilder || isBacktestReport) &&
    currentViewComponentRef?.value?.scorecardsLoading === false
  ) {
    if (isScorecardsTab.value) {
      emit("update-selected-scorecards", newValue);
    } else if (isScorecardGroupsTab.value) {
      emit("update-selected-scorecard-groups", newValue);
    }
  }
});

onBeforeMount(async () => {
  if (
    !isWorkflowLive &&
    !isLendflowUser &&
    !activeClient.value?.can_manage_score_cards
  ) {
    await router.push({ name: ROUTE_PAGE_NOT_FOUND });
  }
  params.value.application_id = getAppIdParam();
  params.value.include_content = props.isView;
});

onUnmounted(() => store.clearViewParams());
</script>
